import { useState, useRef, useCallback } from 'react';
import { Download, Upload, CheckCircle, AlertCircle, FileSpreadsheet, X, RefreshCw } from 'lucide-react';
import { productService } from '../../services/product.service';
import { apiService } from '../../services/api.service';
import { toast } from 'sonner';

interface ProductRow {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  price: string;
  costPrice: string;
  isActive: string;
  image: string;
}

interface ParsedRow extends ProductRow {
  _hasError?: boolean;
  _errorMsg?: string;
}

// ── Minimal CSV parser (handles quoted fields) ────────────────────────────────
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim()) continue;
    const fields: string[] = [];
    let inQuote = false;
    let cur = '';
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = !inQuote;
      } else if (ch === ',' && !inQuote) {
        fields.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    fields.push(cur);
    rows.push(fields);
  }
  return rows;
}

function csvEscape(val: unknown): string {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const CSV_HEADERS: (keyof ProductRow)[] = [
  'id', 'sku', 'name', 'description', 'category', 'price', 'costPrice', 'isActive', 'image',
];

export function ProductsImportExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [importResult, setImportResult] = useState<{ updatedCount: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const res = await productService.getAll({ limit: 1000 });
      if (!res.success || !res.data) throw new Error('Failed to fetch products');

      const products: any[] = Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];

      const lines: string[] = [CSV_HEADERS.join(',')];
      for (const p of products) {
        const row: ProductRow = {
          id: p.id,
          sku: p.sku,
          name: p.name,
          description: p.description ?? '',
          category: p.category?.name ?? '',
          price: String(p.price),
          costPrice: String(p.costPrice ?? ''),
          isActive: p.isActive ? 'TRUE' : 'FALSE',
          image: (p.images?.[0] ?? p.imageMetadata?.unsplashId ?? ''),
        };
        lines.push(CSV_HEADERS.map((h) => csvEscape(row[h])).join(','));
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `menu-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${products.length} products`);
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // ── Parse uploaded file ──────────────────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFileName(file.name);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length < 2) {
        toast.error('CSV appears to be empty');
        return;
      }

      const headers = rows[0].map((h) => h.trim().toLowerCase());
      const idxOf = (key: string) => headers.indexOf(key);
      const idIdx    = idxOf('id');
      const skuIdx   = idxOf('sku');
      const nameIdx  = idxOf('name');
      const descIdx  = idxOf('description');
      const catIdx   = idxOf('category');
      const priceIdx = idxOf('price');
      const cpIdx    = idxOf('costprice');
      const actIdx   = idxOf('isactive');
      const imgIdx   = idxOf('image');

      if (idIdx === -1 || priceIdx === -1) {
        toast.error('CSV must contain at least "id" and "price" columns');
        setParsedRows([]);
        return;
      }

      const parsed: ParsedRow[] = rows.slice(1).map((cols) => {
        const get = (idx: number) => (idx >= 0 ? (cols[idx] ?? '').trim() : '');
        const priceVal = get(priceIdx);
        const hasError = !get(idIdx) || isNaN(parseFloat(priceVal));
        return {
          id: get(idIdx),
          sku: get(skuIdx),
          name: get(nameIdx),
          description: get(descIdx),
          category: get(catIdx),
          price: priceVal,
          costPrice: get(cpIdx),
          isActive: get(actIdx),
          image: get(imgIdx),
          _hasError: hasError,
          _errorMsg: hasError ? (!get(idIdx) ? 'Missing id' : 'Invalid price') : undefined,
        };
      });

      setParsedRows(parsed);
      const errors = parsed.filter((r) => r._hasError).length;
      if (errors > 0) toast.warning(`${errors} row(s) have errors and will be skipped`);
      else toast.success(`${parsed.length} rows ready to import`);
    };
    reader.readAsText(file);
  }, []);

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = useCallback(async () => {
    const validRows = parsedRows.filter((r) => !r._hasError);
    if (validRows.length === 0) return;

    setIsImporting(true);
    try {
      const updates = validRows.map((r) => ({
        id: r.id,
        ...(r.sku ? { sku: r.sku } : {}),
        ...(r.name ? { name: r.name } : {}),
        ...(r.description !== undefined ? { description: r.description } : {}),
        price: parseFloat(r.price),
        ...(r.costPrice ? { costPrice: parseFloat(r.costPrice) } : {}),
        ...(r.isActive
          ? { isActive: r.isActive.toUpperCase() === 'TRUE' || r.isActive === '1' }
          : {}),
      }));

      const res = await apiService.post<{ updatedCount: number }>('/products/bulk-update', { updates });
      if (res.success) {
        setImportResult({ updatedCount: validRows.length });
        setParsedRows([]);
        setFileName('');
        toast.success(`${validRows.length} products updated successfully`);
      } else {
        throw new Error('Bulk update failed');
      }
    } catch {
      toast.error('Import failed. Please try again.');
    } finally {
      setIsImporting(false);
    }
  }, [parsedRows]);

  const validCount  = parsedRows.filter((r) => !r._hasError).length;
  const errorCount  = parsedRows.filter((r) => r._hasError).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu Items Export / Import</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Export your menu items to CSV, edit prices and details in a spreadsheet, then re-upload to apply changes. Existing orders are never affected.
        </p>
      </div>

      {/* Export */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Export Current Menu</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Downloads a CSV with all products: ID, SKU, name, description, category, price, cost price, status, image.
              </p>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-60"
          >
            {isExporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-xs text-amber-700 dark:text-amber-400">
            <strong>Tip:</strong> The <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">id</code> column is required for matching — do not delete or edit it. You can freely edit <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">sku</code>, <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">name</code>, <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">description</code>, <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">price</code>, <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">costPrice</code>, and <code className="bg-amber-100 dark:bg-amber-800/40 px-1 rounded">isActive</code> (TRUE/FALSE). <code>category</code> and <code>image</code> columns are ignored on import.
          </p>
        </div>
      </div>

      {/* Import */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg">
            <Upload className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">Import Updated Menu</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Upload your edited CSV to bulk-update products. Only the rows in the file will be updated.
            </p>
          </div>
        </div>

        {/* Success result */}
        {importResult && (
          <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
            <p className="text-sm text-green-700 dark:text-green-300">
              Successfully updated <strong>{importResult.updatedCount}</strong> products.
            </p>
          </div>
        )}

        {/* Drop zone */}
        {parsedRows.length === 0 && (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging
                ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
              Drop your CSV file here, or <span className="text-indigo-600 dark:text-indigo-400">browse</span>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">.csv files only</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ''; }}
            />
          </div>
        )}

        {/* Preview table */}
        {parsedRows.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{fileName}</p>
                <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full">
                  {validCount} valid
                </span>
                {errorCount > 0 && (
                  <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">
                    {errorCount} errors
                  </span>
                )}
              </div>
              <button
                onClick={() => { setParsedRows([]); setFileName(''); setImportResult(null); }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap"></th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">SKU</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Price</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Cost Price</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Active</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">Category</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {parsedRows.map((row, i) => (
                    <tr
                      key={i}
                      className={row._hasError
                        ? 'bg-red-50 dark:bg-red-900/10'
                        : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }
                    >
                      <td className="px-3 py-2">
                        {row._hasError ? (
                          <span title={row._errorMsg}>
                            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          </span>
                        ) : (
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono">{row.sku}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[180px] truncate">{row.name}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-white">{row.price}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{row.costPrice}</td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          row.isActive.toUpperCase() === 'TRUE' || row.isActive === '1'
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {row.isActive.toUpperCase() === 'TRUE' || row.isActive === '1' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{row.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {validCount} of {parsedRows.length} rows will be applied.
                {errorCount > 0 && ` ${errorCount} invalid rows will be skipped.`}
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setParsedRows([]); setFileName(''); }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting || validCount === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-60"
                >
                  {isImporting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {isImporting ? 'Importing…' : `Apply ${validCount} Updates`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
